import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../config";
import Spinner from "../../components/Spinner/Spinner";
import styles from "../../styles/containers/home/form.module.css";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";

const Form = ({
  setIsModalOpen,
  businessName,
  setBusinessName,
  ownerName,
  setOwnerName,
  phone,
  setPhone,
  email,
  setEmail,
  address,
  setAddress,
  refCode,
  setRefCode,
  businessNameError,
  setBusinessNameError,
  ownerNameError,
  setOwnerNameError,
  phoneError,
  setPhoneError,
  emailError,
  setEmailError,
  addressError,
  setAddressError,
  setLatitude,
  setLongitude,
}) => {
  const [loading1, setLoading1] = useState(false);
  // Referral code is optional and most users don't have one. Hide
  // behind a "יש לך קוד הפניה?" toggle so the form looks shorter,
  // but auto-expand if the field already has a value (e.g. it was
  // pre-filled from `?ref=ABC123` in the URL).
  const [refCodeOpen, setRefCodeOpen] = useState(!!refCode);

  // Address verification — when the input is filled but no place_changed
  // fired (browser autofill, paste, or user typed without picking from
  // the dropdown), `address` / `latitude` / `longitude` stay empty even
  // though the field looks complete. We Google-geocode the typed value
  // on blur and surface a confirmation banner: "Is this the address?"
  // with [Yes] / [No] buttons.
  const [geocodedHint, setGeocodedHint] = useState(null);
  // null | { formatted_address, lat, lng }

  const handleAddressBlur = () => {
    const typed = addressInputRef.current?.value?.trim();
    if (!typed) return;
    // place_changed already fired and populated `address` — nothing to
    // resolve.
    if (address && address === typed) return;
    if (!window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: typed, region: "IL", componentRestrictions: { country: "IL" } },
      (results, status) => {
        if (status !== "OK" || !results?.[0]?.geometry) return;
        const r = results[0];
        setGeocodedHint({
          formatted_address: r.formatted_address,
          lat: r.geometry.location.lat(),
          lng: r.geometry.location.lng(),
        });
      },
    );
  };

  const acceptGeocoded = () => {
    if (!geocodedHint) return;
    setAddress(geocodedHint.formatted_address);
    setLatitude(String(geocodedHint.lat));
    setLongitude(String(geocodedHint.lng));
    if (addressInputRef.current) {
      addressInputRef.current.value = geocodedHint.formatted_address;
    }
    setGeocodedHint(null);
    setAddressError("");
  };

  const rejectGeocoded = () => {
    setGeocodedHint(null);
    setAddress("");
    setLatitude("");
    setLongitude("");
    if (addressInputRef.current) {
      addressInputRef.current.value = "";
      addressInputRef.current.focus();
    }
  };

  const isValidPhoneNumber = (phoneNumber) => {
    const pattern = /^05\d{8}$/;
    return pattern.test(phoneNumber);
  };

  const isValidEmail = (emailAddress) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return pattern.test(emailAddress);
  };

  const handlePhoneInputChange = (e) => {
    const inputPhone = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(inputPhone);
    if (phoneError && inputPhone) setPhoneError("");
  };

  const [sendChannel, setSendChannel] = useState("whatsapp");

  const stepOne = async (e, channel = "whatsapp") => {
    e?.preventDefault();

    if (!businessName) {
      setBusinessNameError(" אנו צריכים לדעת את שם העסק :)");
    }
    if (!ownerName) {
      setOwnerNameError("נשמח לדעת עם מי יש לנו עסק😅");
    }
    if (!phone) {
      setPhoneError("כמעט שכחת להזין מספר טלפון");
    } else if (!isValidPhoneNumber(phone)) {
      setPhoneError("יש להזין מספר טלפון ישראלי תקין (05XXXXXXXX)");
    }
    // Email is optional — only a typed address has to be valid
    if (email && !isValidEmail(email)) {
      setEmailError("כתובת מייל לא תקינה");
    }

    // Address is now OPTIONAL. If the user did type something but
    // didn't pick a Google Places suggestion, still surface the
    // geocode confirmation banner so the lat/lng get filled — but
    // pure-empty is fine, Home.jsx will default it to "ישראל" on
    // submit.
    const typedAddress = addressInputRef.current?.value?.trim() || "";
    if (!address && typedAddress) {
      if (!geocodedHint) {
        // Trigger the geocode + confirmation flow now and stop the
        // submit. The user will accept/reject the suggestion via the
        // banner; pressing "submit" again afterward goes through.
        setAddressError("");
        handleAddressBlur();
        return;
      } else {
        // Banner already up — tell the user to confirm.
        setAddressError("יש לאשר את הכתובת בכפתור הירוק למטה");
        return;
      }
    }
    // Empty address is valid — clear any stale error.
    if (!typedAddress && !address) {
      setAddressError("");
    }

    const isValid = businessName && ownerName
      && phone && isValidPhoneNumber(phone)
      && (!email || isValidEmail(email));
      // Address intentionally NOT in this check — it's optional now.

    if (isValid) {
      const payload = {
        phone,
        business_phone: phone,
        channel,
      };

      setSendChannel(channel);
      setLoading1(true);

      try {
        const res = await axios.post(`${BASE_URL}/business/login`, payload);
        if (res.status === 201) {
          setIsModalOpen(true);
        }
      } catch (error) {
        console.log(error);
      }

      setLoading1(false);
    }
  };

  // HANDLE GOOGLE PLACES API
  const addressInputRef = useRef(null);

  useEffect(() => {
    const autocomplete = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      {
        // `address` (not `geocode`) constrains suggestions to street-level
        // addresses, which is what a business needs. `geocode` was leaking
        // city-only and POI matches that weren't selectable as a precise
        // location.
        types: ["address"],
        componentRestrictions: { country: "IL" },
        fields: ["formatted_address", "geometry"],
      },
    );

    autocomplete.addListener("place_changed", () => {
      const selectedPlace = autocomplete.getPlace();

      // Defensive — if the user pressed Enter on a partial/free-typed
      // value, getPlace() returns no geometry. Skip silently so
      // setLatitude doesn't crash; the blur-handler's geocoder will
      // pick it up and surface a confirmation banner instead.
      if (!selectedPlace?.geometry) return;

      setAddress(selectedPlace.formatted_address || "");
      setLatitude(selectedPlace.geometry.location.lat().toString() || "");
      setLongitude(selectedPlace.geometry.location.lng().toString() || "");
      // User picked from the official Google list — no need to ask
      // them again via the geocoded hint.
      setGeocodedHint(null);
    });

    return () => {
      if (addressInputRef.current) {
        window.google.maps.event.clearInstanceListeners(addressInputRef.current);
      }
    };
  }, []);

  // Inline SVGs — keeps the form self-contained without adding an icon
  // dependency. 18×18 strokes, currentColor so we can recolor via CSS.
  const Icon = {
    user: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    shop: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l1.5-5h15L21 9"/>
        <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9z"/>
        <path d="M9 21V14h6v7"/>
      </svg>
    ),
    mail: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2"/>
        <path d="M3 7l9 6 9-6"/>
      </svg>
    ),
    phone: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
    pin: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
    gift: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 12 20 22 4 22 4 12"/>
        <rect x="2" y="7" width="20" height="5"/>
        <line x1="12" y1="22" x2="12" y2="7"/>
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
      </svg>
    ),
  };

  return (
    <div className={styles.container}>
      <div className={styles.mobileText}>
        <h2>מקימים עסק בקליק תוך דקה</h2>
        <p>
          מערכת ניהול תורים חכמה לכל עסק. הלקוחות שלכם קובעים תור בקלות, ואתם מנהלים הכל ממקום אחד
        </p>
      </div>

      <div className={styles.headerRow}>
        <span className={styles.headerIcon}>{Icon.user}</span>
        <h2>צור חשבון חדש</h2>
      </div>
      <p className={styles.signinHint}>
        כבר יש לך חשבון? <a href="https://dash.tor.digital" target="_blank" rel="noreferrer">התחבר כאן</a>
      </p>

      <form className={styles.form} onSubmit={stepOne}>
        <div className={styles.field}>
          <label>
            שם העסק
            <span> *</span>
          </label>
          <div className={styles.inputWrap}>
            <span className={styles.inputIcon}>{Icon.shop}</span>
            <input
              type="text"
              name="businessName"
              placeholder="הזן שם העסק"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
            />
          </div>
          {businessNameError && (
            <small className={styles.errorMsg}>{businessNameError}</small>
          )}
        </div>

        <div className={styles.field}>
          <label>
            השם שלך
            <span> *</span>
          </label>
          <div className={styles.inputWrap}>
            <span className={styles.inputIcon}>{Icon.user}</span>
            <input
              type="text"
              name="ownerName"
              placeholder="הזן את שמך המלא"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
          </div>
          {ownerNameError && (
            <small className={styles.errorMsg}>{ownerNameError}</small>
          )}
        </div>

        <div className={styles.field}>
          <label>
            כתובת מייל
            <span className={styles.optional}> (לא חובה)</span>
          </label>
          <div className={styles.inputWrap}>
            <span className={styles.inputIcon}>{Icon.mail}</span>
            <input
              type="email"
              name="email"
              placeholder="הזן את כתובת המייל שלך"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(""); }}
            />
          </div>
          {emailError && (
            <small className={styles.errorMsg}>{emailError}</small>
          )}
        </div>

        <div className={styles.field}>
          <label>
            טלפון
            <span> *</span>
          </label>
          <div className={styles.inputWrap}>
            <span className={styles.inputIcon}>{Icon.phone}</span>
            <input
              type="tel"
              name="phone"
              placeholder="הזן מספר טלפון"
              value={phone}
              onChange={handlePhoneInputChange}
            />
          </div>
          {phoneError && (
            <small className={styles.errorMsg}>{phoneError}</small>
          )}
        </div>

        <div className={styles.field}>
          <label>
            כתובת של העסק
            <span className={styles.optional}> (לא חובה אבל מומלץ 🙂)</span>
          </label>
          <div className={styles.inputWrap}>
            <span className={styles.inputIcon}>{Icon.pin}</span>
            <input
              type="text"
              name="address"
              placeholder="השאירו ריק או הקלידו את הכתובת המדויקת"
              ref={addressInputRef}
              onBlur={handleAddressBlur}
              // Disable browser autofill so it doesn't compete with the
              // Google Places suggestions dropdown. Random non-standard
              // value tells Chrome/Safari/Firefox not to remember or
              // suggest entries for this field. Also disable correction
              // and capitalization which can fight with Google's results.
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
          </div>
          {!geocodedHint && (
            <small className={styles.helperText}>
              אפשר להשאיר ריק — נציג ״ישראל״ כברירת מחדל. אם תרצו, התחילו להקליד ובחרו מהרשימה.
            </small>
          )}
          {/* Confirmation banner — fired when the input has text but no
              place_changed event populated the canonical lat/lng (most
              commonly: browser autofill or user pressed Tab without
              clicking a suggestion). Asks the user to confirm Google's
              best guess for the typed value before we accept it. */}
          {geocodedHint && (
            <div className={styles.addressConfirm}>
              <div className={styles.addressConfirmTitle}>האם זו הכתובת?</div>
              <div className={styles.addressConfirmText}>
                {geocodedHint.formatted_address}
              </div>
              <div className={styles.addressConfirmActions}>
                <button
                  type="button"
                  className={styles.addressConfirmYes}
                  onClick={acceptGeocoded}>
                  ✓ כן, זוהי הכתובת
                </button>
                <button
                  type="button"
                  className={styles.addressConfirmNo}
                  onClick={rejectGeocoded}>
                  לא, אבחר אחרת
                </button>
              </div>
            </div>
          )}
          {addressError && (
            <small className={styles.errorMsg}>{addressError}</small>
          )}
        </div>

        {/* Referral code — optional, collapsed by default. Expands
            when the user clicks the toggle, or auto-expands if
            already pre-filled from the URL (`?ref=...`). */}
        {!refCodeOpen ? (
          <button
            type="button"
            className={styles.refToggle}
            onClick={() => setRefCodeOpen(true)}>
            <span className={styles.refToggleIcon}>{Icon.gift}</span>
            <span>יש לך קוד הפניה?</span>
          </button>
        ) : (
          <div className={styles.field}>
            <label>
              קוד הפניה
              <span className={styles.optional}> (לא חובה)</span>
            </label>
            <div className={styles.inputWrap}>
              <span className={styles.inputIcon}>{Icon.gift}</span>
              <input
                type="text"
                name="refCode"
                placeholder="קוד שקיבלת ממכר/חבר"
                value={refCode || ""}
                onChange={(e) => setRefCode(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck="false"
                style={{ textTransform: "uppercase" }}
              />
            </div>
            <small className={styles.helperText}>
              אם נרשמת דרך הפניה ממישהו, הזן את הקוד שלו כאן.
            </small>
          </div>
        )}

        <p className={styles.termsHint}>
          בלחיצה על הרשמה, אני מאשר/ת את <a href="https://tor.digital/privacy-policy" target="_blank" rel="noreferrer">תנאי השימוש</a> ו<a href="https://tor.digital/privacy-policy" target="_blank" rel="noreferrer">מדיניות הפרטיות</a>
        </p>

        <button
          type="button"
          className={styles.btn}
          onClick={(e) => stepOne(e, "whatsapp")}
          disabled={loading1}
        >
          {loading1 ? <Spinner /> : "הרשמה בחינם"}
        </button>
      </form>
    </div>
  );
};

export default Form;
